import { $fetch, type FetchOptions } from 'ofetch';
import type { Model } from './Model';
import { defineApiUrl } from './utils/defineApiUrl';
import { defineUri } from './utils/defineUri';
import type { TOverride } from './utils/types';
import { ApiResponse, IPagination, PaginatedResponse, SimpleResponse } from './ApiResponse';
import { apiConfig } from './defineConfig';
import { createInstance } from './utils/createInstance';
import { deepMerge } from './utils/deepMerge';

export type TApiOptions = FetchOptions<'json'>;

type TMethod = (...args: any[]) => void;

type TMethods = Record<string, TMethod>;

type TSetupEndpoints<E> = TMethods extends E ? {} : E;

type TIdentifier =
  | string
  | number
  ;

export interface IBaseModelApi<M> {
  fetchAll(options?: TApiOptions): Promise<ApiResponse<M[]>>
  fetchOne(id: TIdentifier, options?: TApiOptions): Promise<ApiResponse<M>>;
  fetchMany(ids: TIdentifier[], options?: TApiOptions): Promise<ApiResponse<M[]>>;
  createOne<Request extends Partial<M>>(data: Request, options?: TApiOptions): Promise<ApiResponse<M>>;
  updateOne(id: TIdentifier, data: Partial<M>, options?: TApiOptions): Promise<ApiResponse<M>>;
  updateMany(ids: TIdentifier[], data: Partial<M>, options?: TApiOptions): Promise<ApiResponse<M[]>>;
  deleteOne(id: TIdentifier, options?: TApiOptions): Promise<ApiResponse<null>>;
  deleteMany(ids: TIdentifier[], options?: TApiOptions): Promise<ApiResponse<null>>;
}

interface ISetupContext<M> extends IBaseModelApi<M> {
  request<T>(url: string, options?: TApiOptions): Promise<ApiResponse<T>>;
  rawRequest<T = TResponse>(url: string, options?: TApiOptions): Promise<T>;
}

type TEndpointsContext<M, E> =
  & E
  & ThisType<ISetupContext<M> & E>
  ;

type TResponse = any;

type TPaginationMapper = (response: TResponse) => IPagination;

type TPaginationKeys = {
  [K in keyof IPagination]: string;
};

export interface IPaginationSetup extends Partial<TPaginationKeys> {
  dataWrapper?: string;
  paginationWrapper?: string;
  mapper?: TPaginationMapper;
}

export interface ISetup<M, E> {
  apiUrl?: (() => string) | string;
  uri?: (() => string) | string;
  trimSlashes?: boolean;
  endpoints?: TEndpointsContext<M, E>;
  pagination?: IPaginationSetup;
  options?: TApiOptions;
}

export type TApi<M, E> =
  & E
  & IBaseModelApi<M>
  & TOverride<IBaseModelApi<M>, TSetupEndpoints<E>>
;

export interface IApiDefinition<M, E> {
  (): TApi<M, E>;
}

interface IProcessedPaginatedResponse<T> {
  data: T;
  pagination: IPagination;
}

interface ISetupConfig {
  getUri(): string;
  getApiUrl(): string;
  buildFullUrl(...segments: string[]): string;
  getPaginationWrapperKey(): string;
  getPaginationDataKey(): string;
  processPaginatedResponse<T>(response: TResponse): IProcessedPaginatedResponse<T>;
  getApiOptions(): TApiOptions;
}

function getSetupConfig<M extends Model, E = {}>(ModelClass: new () => M, setup?: ISetup<M, E>): ISetupConfig {
  const getUri = defineUri(ModelClass, setup);
  const getApiUrl = defineApiUrl(setup);

  function buildFullUrl(...segments: string[]): string {
    const apiUrl = getApiUrl();
    const uri = getUri();
    const concatenated = [apiUrl, uri, ...segments].join('/');
    const trimmed = (setup?.trimSlashes ?? true)
      ? concatenated.replace(/([^:]\/)\/+/g, '$1')
      : concatenated;

    return trimmed
      .replace(/^\/*/gi, '')
      .replace(/\/*$/gi, '');
  }

  function processPaginatedResponse<T>(response: TResponse): IProcessedPaginatedResponse<T> {
    const paginationTemplate: IPaginationSetup | undefined = setup?.pagination ?? apiConfig.pagination;
    const dataWrapperKey = getPaginationDataKey();
    const data = response[dataWrapperKey].map((data: any) => createInstance(ModelClass, data));

    if (paginationTemplate) {
      if (paginationTemplate.mapper) {
        return {
          data,
          pagination: paginationTemplate.mapper(response),
        };
      }

      const pagination = paginationTemplate.paginationWrapper
        ? response[paginationTemplate.paginationWrapper]
        : response.pagination;

      return {
        data,
        pagination: {
          currentPage: pagination[paginationTemplate.currentPage ?? 'currentPage'],
          lastPage: pagination[paginationTemplate.lastPage ?? 'lastPage'],
          perPage: pagination[paginationTemplate.perPage ?? 'perPage'],
          total: pagination[paginationTemplate.total ?? 'total'],
        },
      };
    }

    return {
      data: response.data,
      pagination: response.pagination,
    };
  }

  function getPaginationDataKey(): string {
    return setup?.pagination?.dataWrapper ?? apiConfig.pagination?.dataWrapper ?? 'data';
  }

  function getPaginationWrapperKey(): string {
    return setup?.pagination?.paginationWrapper ?? apiConfig.pagination?.paginationWrapper ?? 'pagination';
  }

  function getApiOptions(): TApiOptions {
    return setup?.options ?? {};
  }

  return {
    getUri,
    getApiUrl,
    buildFullUrl,
    processPaginatedResponse,
    getPaginationDataKey,
    getPaginationWrapperKey,
    getApiOptions,
  };
}

function getConfigHeaders(): HeadersInit {
  if (apiConfig.headers) {
    return typeof apiConfig.headers === 'function'
      ? apiConfig.headers()
      : apiConfig.headers;
  }
  return {};
}

export function buildOptions(...options: TApiOptions[]): TApiOptions {
  const mergedOptions: TApiOptions = {};
  deepMerge(mergedOptions, apiConfig?.requestOptions ?? {});
  deepMerge(mergedOptions, {
    headers: {
      'Accept': 'application/json',
      ...getConfigHeaders(),
    },
  });
  options.forEach((options) => {
    deepMerge(mergedOptions, options);
  });
  return mergedOptions;
}

function getBaseEndpoints<M extends Model>(ModelClass: new () => M, setupConfig: ISetupConfig): ISetupContext<M> {
  function isPaginatedResponse(response: TResponse): boolean {
    const paginationDataKey = setupConfig.getPaginationDataKey();
    const paginationWrapperKey = setupConfig.getPaginationWrapperKey();

    return (
      typeof response === 'object' &&
      response !== null &&
      paginationWrapperKey in response &&
      paginationDataKey in response
    );
  }

  class ResponseError extends Error {
    public constructor(message: string) {
      super(setupConfig.buildFullUrl() + ': ' + message);
    }
  }

  class InvalidResponseError extends ResponseError {
    public constructor(response: Response) {
      super(`Invalid response. Expected pagination object or array, got "${JSON.stringify(response)}".`);
    }
  }

  return {
    async rawRequest<T = TResponse>(url: string, options: TApiOptions = {}): Promise<T> {
      return await $fetch<T>(setupConfig.buildFullUrl(url), buildOptions(setupConfig.getApiOptions(), options));
    },
    async request<T>(url: string, options?: TApiOptions): Promise<ApiResponse<T>> {
      const response = await this.rawRequest(url, options);
      if (isPaginatedResponse(response)) {
        const processedResponse = setupConfig.processPaginatedResponse<M[]>(response);
        return new PaginatedResponse(
          processedResponse.data.map((item: M) => createInstance(ModelClass, item)),
          processedResponse.pagination,
        ) as ApiResponse<T>;
      } else if (Array.isArray(response)) {
        return new SimpleResponse(response.map((item: M) => createInstance(ModelClass, item))) as ApiResponse<T>;
      }
      throw new InvalidResponseError(response);
    },
    async fetchAll(options?: TApiOptions): Promise<ApiResponse<M[]>> {
      return await this.request('', {
        method: 'GET',
        ...options,
      });
    },
    async fetchOne(id: TIdentifier, options?: TApiOptions): Promise<ApiResponse<M>> {
      return await this.request(id.toString(), {
        method: 'GET',
        ...options,
      });
    },
    async fetchMany(ids: TIdentifier[], options?: TApiOptions): Promise<ApiResponse<M[]>> {
      return await this.request('', {
        method: 'GET',
        ...options,
        body: {
          ids,
        },
      });
    },
    async createOne<Request extends Partial<M>>(data: Request, options?: TApiOptions): Promise<ApiResponse<M>> {
      return await this.request('', {
        method: 'POST',
        ...options,
        body: data,
      });
    },
    async updateOne(id: TIdentifier, data: Partial<M>, options?: TApiOptions): Promise<ApiResponse<M>> {
      return await this.request(id.toString(), {
        method: 'PATCH',
        ...options,
        body: data,
      });
    },
    async updateMany(ids: TIdentifier[], data: Partial<M>, options?: TApiOptions): Promise<ApiResponse<M[]>> {
      return await this.request('', {
        method: 'PATCH',
        ...options,
        body: {
          ids,
          data,
        },
      });
    },
    async deleteOne(id: TIdentifier, options?: TApiOptions): Promise<ApiResponse<null>> {
      return await this.request(id.toString(), {
        method: 'DELETE',
        ...options,
      });
    },
    async deleteMany(ids: TIdentifier[], options?: TApiOptions): Promise<ApiResponse<null>> {
      return await this.request('', {
        method: 'DELETE',
        ...options,
        body: {
          ids,
        },
      });
    },
  };
}

export function defineApi<M extends Model, E = {}>(ModelClass: new () => M, setup?: ISetup<M, E>): IApiDefinition<M, E> {
  const setupConfig = getSetupConfig(ModelClass, setup);
  const baseEndpoints = getBaseEndpoints(ModelClass, setupConfig);
  const endpointsWithBaseContext = {} as E;

  let setupEndpoints = {} as E;
  if (setup && setup.endpoints) {
    setupEndpoints = setup.endpoints;
  }

  Object.keys(setupEndpoints as object).forEach((key: string) => {
    const setupKey = key as keyof typeof setupEndpoints;
    const method = setupEndpoints[setupKey] as TMethod;
    endpointsWithBaseContext[setupKey] = method.bind({
      ...baseEndpoints,
      ...setupEndpoints,
    }) as E[keyof E];
  });

  // We must return a named function, so it can be automatically detected in the controller
  return function useApi() {
    return {
      ...baseEndpoints,
      ...endpointsWithBaseContext,
    };
  };
}

export const defineApiExportsForTesting = {
  getSetupConfig,
};
